import { useState, useEffect, useRef } from 'react';
import { useReduxState } from 'hooks';
import { Alert } from 'components/common';
import { SelectInput } from 'components/common/form/common';
import { EditorBlockFillinAnswerTranslation } from '.';

import type { BlockFillin } from 'shared-types';
import type { BlockFillInStateProps } from './EditorBlockFillIn';

interface Props extends BlockFillInStateProps {
  data: BlockFillin['correctAnswers'][number];
  locales: BlockFillin['locales'];
  languageVersion: string;
  id: string;
  index: number;
  activeContentId?: string | null;
  onInputChange: (index: number, mappedData: { value: string }[]) => void;
  onFocus: (id: string) => void;
  onTranslationChange: (
    answerId: string,
    optionIndex: number,
    newValue: string
  ) => void;
}

const EditorBlockFillInAnswer = ({
  data,
  locales,
  languageVersion,
  id,
  index,
  activeContentId,
  onInputChange,
  onFocus,
  onTranslationChange,
  ...stateProps
}: Props) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, theme } = useReduxState();

  const selectInputRef = useRef(null);
  const selectElementRef = useRef(null);

  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);

  // focus the input if the component's data id matches the activeContentId
  useEffect(() => {
    if (data.id === activeContentId && selectInputRef.current) {
      // there seems to be a bug with the event sequences after
      // calling .focus() on an element in Firefox. It often
      // triggers the onBlur after setting focused, reverting the
      // effects. Resetting focus on a component update, on a very
      // short time window, seems to circunvent this bug...
      // https://bugzilla.mozilla.org/show_bug.cgi?id=375751
      setTimeout(() => {
        selectInputRef.current.focus();
      }, 10);
    }
  }, [activeContentId, data.id]);

  const handleChange = (options) => {
    if (isEditingLanguageVersion) return;

    const mappedData = options.map((option) => ({ value: option.value }));
    onInputChange(index, mappedData);
  };

  const handleFocus = () => {
    if (isEditingLanguageVersion) return;
    onFocus(data.id);
  };

  const handleBlur = () => {
    setHasBeenBlurred(true);
  };

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="answer" data-id={data.id}>
      <div className="content">
        {isEditingLanguageVersion ? (
          <div className="translation-inputs">
            {data.options.map((_, optionIndex) => {
              const localeKey = `correctAnswers_${data.id}_${
                optionIndex + 1
              }` as const;

              const currentValue =
                locales[languageVersion]?.translatable?.[localeKey] || '';

              const referenceValue =
                locales[editorLanguageReference]?.translatable?.[localeKey] ||
                '';

              return (
                // render individual answers in input fields when translating a language version
                <EditorBlockFillinAnswerTranslation
                  key={optionIndex}
                  initialValue={currentValue}
                  optionId={data.id}
                  optionIndex={optionIndex}
                  referenceValue={referenceValue}
                  onChange={onTranslationChange}
                  onFocus={onFocus}
                />
              );
            })}
          </div>
        ) : (
          <SelectInput
            hideDropdownIndicator={true}
            id={id}
            inputId={`${id}-input`}
            creatable={true}
            isClearable={false}
            isMulti={true}
            isDisabled={isEditingLanguageVersion}
            menuWidth="360px"
            noOptionsMessage={() => null}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={messages.commonAddAnswer}
            selectElementRef={(el) => (selectElementRef.current = el)}
            selectRef={(el) => (selectInputRef.current = el)}
            style={{ width: '100%' }}
            value={
              data.options
                ? data.options.map((_, index) => {
                    const value =
                      locales[languageVersion]?.translatable?.[
                        `correctAnswers_${data.id}_${index + 1}`
                      ] || '';

                    return { label: value, value };
                  })
                : null
            }
          />
        )}
      </div>

      {hasBeenBlurred && !data.options.length && !isEditingLanguageVersion ? (
        <Alert
          text={messages.editorBlockFillInAnswerWarning}
          size="small"
          style={{ margin: '0 0 10px 20px' }}
          type="error-light"
        />
      ) : null}

      <style jsx>{`
        .answer {
          background-color: white;
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${isEditingLanguageVersion
            ? theme.cardBorderRadius
            : theme.inputBorderRadius};
          padding: ${isEditingLanguageVersion ? '5px 10px' : '0px 10px'};
          transition: border-color 0.3s;
        }
        .answer:focus-within {
          border-color: #a1a8aa;
        }
        .answer:not(:first-child) {
          margin-top: 8px;
        }
        .content {
          align-items: top;
          display: flex;
          flex-direction: row;
          position: relative;
        }
        .content::before {
          counter-increment: fillin-answers;
          content: counter(fillin-answers) '.';
          display: inline-block;
          font-weight: 400;
          margin-right: 5px;
          padding: 12px 0;
        }
        .content :global(.react-select__control) {
          border: 0;
        }
        .content :global(.react-select__placeholder),
        .content :global(.react-select__input-container) {
          padding: 8px 0;
        }
        .translation-inputs {
          width: 100%;
        }
        .translation-input {
          margin-bottom: 8px;
        }
        .translation-input:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockFillInAnswer;
